import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik, setIn } from 'formik';

import CommunicationActionSendNew from '~/components/communications/CommunicationCard/CommunicationActionSendNew';
import useCreateCommunication from '~/components/communications/CommunicationCard/useCreateCommunication';
import SmsCommunicationSpecificBody from '~/components/communications/SmsCommunicationCard/SmsCommunicationSpecificBody';
import { getSmsCharacterLimit, getSmsMessageValidation } from '~/components/communications/SmsCommunicationCard/utils';
import { FROM_TEMPLATE_FORM_KEYS } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import useOrganization from '~/components/OrganizationContext';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { getExposuresArrayFromClaimByIds, reportAxiosError } from '~/Utils';

import { useClaim } from '../../ClaimContainer';
import mixpanel from '../../CmsMain/mixpanel';
import LoadingDialog from '../../LoadingDialog';
import useDataFetcher from '../../useDataFetcher';
import CommunicationCard from '../CommunicationCard';

function SendSmsCommunicationCardContainer(props) {
  const {
    contact,
    contactPhoneId,
    onCancel,
    onSendSms,
    messageContent,
    onMinimized,
    disableMinimized,
    open,
    communicationSuggestionNotificationId = null,
  } = props;
  const { communicationConfiguration } = useOrganization();
  const isCategoryRequired = communicationConfiguration?.is_category_required ?? false;
  const validationSchema = getSmsMessageValidation({ isCategoryRequired });

  const { claim } = useClaim();
  const {
    isLoading,
    isError,
    data: userSignatureData,
  } = useDataFetcher(`/api/v1/organizations/${claim.organization_id}/sms_signature`, null, {
    // Same reason as the guard below: the signature is a prefill, so its failure must not put a
    // modal in front of someone who opened this card to send an SMS. reportAxiosError's default
    // opens that dialog; the /api/v1/errors report is unaffected and stands as the record, since
    // the flag also gates Sentry.captureException.
    shouldOpenErrorDialog: false,
  });
  const { communicationCommonInitialValues } = useCreateCommunication('sms', { contact });

  if (isLoading) {
    return <LoadingDialog isError={isError} track="Send Sms" />;
  }

  // A failed signature fetch must not take the card down -- isLoading is already false by then, so
  // the guard above does not cover it. The signature is a prefill, and the route itself answers with
  // an empty one for an organization that has no SMS template.
  const userSignature = userSignatureData?.user_sms_signature ?? '';

  return (
    <Formik
      initialValues={{
        ...communicationCommonInitialValues,
        message_content:
          messageContent === undefined
            ? `Regarding claim ${claim.claim_id_display} with ${claim.policy.client},\n\n\n\n${userSignature}`
            : messageContent,
        phone_id: contactPhoneId ?? contact?.phones?.[0]?.id,
        [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_ID]: null,
        [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT]: null,
        communication_suggestion_claim_notification_id: communicationSuggestionNotificationId,
      }}
      validationSchema={validationSchema}
      onSubmit={(values, formikProps) => {
        return axios
          .post(
            `/api/v1/claims/${claim.id}/contacts/${values.contact.id}/phones/${values['phone_id']}/send_sms`,
            values
          )
          .then((res) => {
            formikProps.setSubmitting(false);
            onSendSms(res.data);

            const exposures = getExposuresArrayFromClaimByIds(claim, values.exposure_ids);
            mixpanel.track(MIXPANEL_EVENTS.SMS_COMMUNICATION_SENT, {
              ...mixpanel.getMixpanelAttributes(claim, exposures, {
                communication_id: res.data.id,
                characters: values.message_content?.length,
                character_limit: getSmsCharacterLimit(values.message_content),
                status: res.status === 204 ? 'success' : 'failed',
                http_response_code: res.status,
              }),
            });
          })
          .catch((error) => {
            formikProps.setSubmitting(false);
            reportAxiosError(error);
          });
      }}
    >
      {(formikProps) => {
        const { values } = formikProps;

        return (
          <CommunicationCard
            title="New Text Message"
            communicationInitialFields={{
              communicationChannel: 'sms',
              communicationContact: values.contact,
              communicationDirection: 'Outgoing',
            }}
            communicationHeaderAdditionalProps={{
              isDynamicContact: !contact,
              dynamicContactLabel: 'Contact',
              getValuesOnSelectContact: (contact, valuesWithContact) =>
                setIn(
                  valuesWithContact,
                  'phone_id',
                  !contact || contact.id === '' || !contact.phones?.length ? '' : contact.phones[0].id
                ),
            }}
            CommunicationTypeSpecificBody={SmsCommunicationSpecificBody}
            communicationTypeSpecificBodyProps={{
              signature: userSignature,
            }}
            isDialog
            isNew
            onClose={onCancel}
            CommunicationAction={CommunicationActionSendNew}
            communicationActionProps={{
              onSubmit: () => {
                formikProps.handleSubmit();
              },
              isSubmitting: formikProps.isSubmitting,
            }}
            onMinimized={onMinimized}
            disableMinimized={disableMinimized}
            open={open}
          />
        );
      }}
    </Formik>
  );
}

SendSmsCommunicationCardContainer.propTypes = {
  contact: PropTypes.object,
  contactPhoneId: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onSendSms: PropTypes.func.isRequired,
  messageContent: PropTypes.string,
  open: PropTypes.bool,
  disableMinimized: PropTypes.bool,
  onMinimized: PropTypes.func,
  communicationSuggestionNotificationId: PropTypes.number,
};

export default SendSmsCommunicationCardContainer;
